/**
 * Planning board (epic-6 §3b / PRJ-4) — pure grouping helpers.
 * The 5 board columns mirror the backend task status enum.
 */
import type { Task, TaskStatus } from '@/types';

export const BOARD_STATUSES: TaskStatus[] = [
  'pending',
  'in_progress',
  'blocked',
  'completed',
  'cancelled',
];

export const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export interface BoardGrouped {
  pending: Task[];
  in_progress: Task[];
  blocked: Task[];
  completed: Task[];
  cancelled: Task[];
}

export function emptyBoard(): BoardGrouped {
  return {
    pending: [],
    in_progress: [],
    blocked: [],
    completed: [],
    cancelled: [],
  };
}

/** Group tasks into the 5 status columns; unknown statuses are dropped. */
export function groupTasksByStatus(tasks: Task[] | undefined | null): BoardGrouped {
  const grouped = emptyBoard();
  if (!Array.isArray(tasks)) return grouped;
  for (const task of tasks) {
    if (task.status && task.status in grouped) {
      grouped[task.status as TaskStatus].push(task);
    }
  }
  for (const col of BOARD_STATUSES) {
    grouped[col].sort((a, b) => Number(b.depth ?? 0) - Number(a.depth ?? 0) || a.createdAt.localeCompare(b.createdAt));
  }
  return grouped;
}

/** Count dashboards for a task: repeats the depth as a "─" chain. */
export function depthBadge(depth: number | undefined): string {
  const d = Math.max(0, Number(depth) || 0);
  return '─'.repeat(Math.min(d, 8));
}

/** Result of splitting tasks into the 5 status columns + custom board columns. */
export interface BoardSplit {
  byStatus: BoardGrouped;
  /** Custom column name → tasks placed there via `boardColumn` (WS-D / PRJ-4). */
  custom: Record<string, Task[]>;
}

/**
 * Split tasks for board rendering: a task with a non-empty `boardColumn`
 * goes to that named column (instead of its status column); everything else
 * falls back to `groupTasksByStatus`. Mirrors backend `getBoard` semantics.
 */
export function splitBoard(tasks: Task[] | undefined | null): BoardSplit {
  const byStatus = emptyBoard();
  const custom: Record<string, Task[]> = {};
  const statusInput: Task[] = [];
  for (const task of tasks ?? []) {
    const col = typeof task.boardColumn === 'string' ? task.boardColumn.trim() : '';
    if (col) (custom[col] ??= []).push(task);
    else statusInput.push(task);
  }
  const grouped = groupTasksByStatus(statusInput);
  for (const col of BOARD_STATUSES) byStatus[col] = grouped[col];
  for (const col of Object.keys(custom)) {
    custom[col].sort((a, b) => Number(b.depth ?? 0) - Number(a.depth ?? 0) || a.createdAt.localeCompare(b.createdAt));
  }
  return { byStatus, custom };
}